import React, { useState, useEffect } from 'react';
import { getCoordinatesForPostcode } from '../../utils/locationUtils';

/**
 * Debug component to display postcode and coordinates information
 */
const LocationDebug = ({ postcode }) => {
  const [coordinates, setCoordinates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('');

  useEffect(() => {
    const fetchCoordinates = async () => {
      if (!postcode) {
        setCoordinates(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Add a listener to console.log to capture the source of coordinates
        const originalConsoleLog = console.log;
        let capturedSource = '';
        
        console.log = (...args) => {
          originalConsoleLog(...args);
          
          // Capture the source of coordinates from log messages
          const message = args.join(' ');
          if (message.includes('Found coordinates for postcode')) {
            if (message.includes('from API')) {
              capturedSource = 'Google Maps API';
            } else if (message.includes('in mock data')) {
              capturedSource = 'Mock Data';
            }
          } else if (message.includes('Using generated coordinates')) {
            capturedSource = 'Generated (deterministic)';
          } else if (message.includes('Using cached coordinates')) {
            capturedSource = 'Cache';
          }
        };

        // Get coordinates
        const coords = await getCoordinatesForPostcode(postcode);
        
        // Restore original console.log
        console.log = originalConsoleLog;
        
        setCoordinates(coords);
        setSource(capturedSource);
      } catch (err) {
        console.error('Error fetching coordinates for debug:', err);
        setError('Failed to fetch coordinates');
      } finally {
        setLoading(false);
      }
    };

    fetchCoordinates();
  }, [postcode]);

  if (!postcode) {
    return (
      <div className="fixed bottom-0 right-0 bg-gray-800 text-white p-4 m-4 rounded-lg shadow-lg z-50 text-xs opacity-80 max-w-xs">
        <h3 className="font-bold mb-2">Location Debug</h3>
        <p>No postcode provided</p>
      </div>
    );
  }

  return (
    <div className="fixed hidden bottom-0 right-0 bg-gray-800 text-white p-4 m-4 rounded-lg shadow-lg z-50 text-xs opacity-80 max-w-xs">
      <h3 className="font-bold mb-2">Location Debug</h3>
      <p><strong>Postcode:</strong> {postcode}</p>
      
      {loading ? (
        <p>Loading coordinates...</p>
      ) : error ? (
        <p className="text-red-400">{error}</p>
      ) : coordinates ? (
        <>
          <p><strong>Latitude:</strong> {coordinates.lat}</p>
          <p><strong>Longitude:</strong> {coordinates.lng}</p>
          <p><strong>Source:</strong> {source}</p>
        </>
      ) : (
        <p>No coordinates found</p>
      )}
    </div>
  );
};

export default LocationDebug;
